import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useAuthUser } from "@/core/context/AuthUserContext"
import { useFormStore } from "@/core/form/store/FormStore"
import { MakeProductTeacherButtonFragment$key } from "@/product/member/admin/list/card/more-actions/make-product-teacher/__generated__/MakeProductTeacherButtonFragment.graphql"
import {
  MakeProductTeacherButtonMutation,
  ProductRole,
} from "@/product/member/admin/list/card/more-actions/make-product-teacher/__generated__/MakeProductTeacherButtonMutation.graphql"
import { useProductRoleLabel } from "@/role/roleUtils"
import { displaySuccessToast } from "@components/toast/ToastProvider"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import DiscoWarningModal from "@disco-ui/modal/DiscoWarningModal"
import { wordStartsWithVowel } from "@utils/string/stringUtils"
import { useState } from "react"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"
interface MakeProductTeacherButtonProps {
  productMembershipKey: MakeProductTeacherButtonFragment$key
  children: OverridableDiscoButtonChildren
  productRole: Exclude<ProductRole, "member">
}

function MakeProductTeacherButton({
  productMembershipKey,
  children,
  productRole,
}: MakeProductTeacherButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { authUser } = useAuthUser({ required: true })

  const activeOrganization = useActiveOrganization()!

  const productMembership = useFragment<MakeProductTeacherButtonFragment$key>(
    graphql`
      fragment MakeProductTeacherButtonFragment on ProductMembership {
        id
        email
        member {
          fullName
        }
        product {
          id
          name
          type
        }
      }
    `,
    productMembershipKey
  )

  const form = useFormStore<MakeProductTeacherButtonMutation>(
    graphql`
      mutation MakeProductTeacherButtonMutation($input: CreateInvitationInput!) {
        response: createInvitations(input: $input) {
          data {
            edges {
              node {
                id
                ...ProductInvitationReportTableRow_Invitation
                ...CommunityInvitationReportTableRow_Invitation
                productMembership {
                  id
                  ...ExperienceAdminsListItemFragment
                }
              }
            }
          }
          errors {
            field
            message
          }
        }
      }
    `,
    {
      productId: productMembership.product.id,
      organizationId: activeOrganization.id,
      message: getDefaultMessage(),
      users: [{ email: productMembership.email! }],
      productRole,
    }
  )

  const { product, member } = productMembership
  const roleLabel = useProductRoleLabel(productRole)
  const roleArticle = wordStartsWithVowel(roleLabel.singular) ? "an" : "a"

  return (
    <>
      <OverridableDiscoButton onClick={openModal}>{children}</OverridableDiscoButton>

      <DiscoWarningModal
        testid={"ProductMemberCard.make-teacher-warning-modal"}
        variant={"primary"}
        icon={"user"}
        isOpen={isModalOpen}
        onClose={closeModal}
        title={`Change role to ${roleLabel.singular}?`}
        confirmationButtonProps={{
          onClick: handleSubmit,
          shouldDisplaySpinner: form.isSubmitting,
          children: `Yes, make ${roleLabel.singular}`,
        }}
        description={`Are you sure you want to make ${member.fullName} ${roleArticle} ${roleLabel.singular}?`}
        modalContentLabel={`Make ${roleLabel.singular} dialog`}
      />
    </>
  )

  async function handleSubmit() {
    const { didSave } = await form.submit(
      {
        productId: form.state.productId,
        organizationId: form.state.organizationId,
        message: form.state.message,
        productRole: form.state.productRole,
        users: form.state.users,
      },
      {
        updater: (store) => {
          const productMembershipRecord = store.get(productMembership.id)

          if (!productMembershipRecord) return
          productMembershipRecord.setValue(productRole, "role")
        },
      }
    )

    if (!didSave) return

    closeModal()
    displaySuccessToast({
      message: `Role changed to ${roleLabel.singular}!`,
      testid: "AdminCardDropdown.success-message",
    })
  }

  function getDefaultMessage() {
    if (!product) return ""

    const hostName = authUser.fullName
    return `Hi! ${hostName} from ${activeOrganization.name} has invited you to ${product.name}, as ${roleArticle} ${roleLabel.singular}.`
  }

  function openModal() {
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
  }
}

export default MakeProductTeacherButton
